// - Renders product details including name, image, and price.
// - Displays a "Sale" badge for products categorized under "Sale" with localized text.
// - Wraps the entire tile in a clickable `Link` component for navigation.
// - Uses responsive styling for alignment and spacing within a compact card layout.
// - Integrates with the `Prices` component to display pricing details, including potential discounts.

import React, { useEffect, useMemo, useState } from 'react';
import { offer, standalonePrice, swatches } from './type';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { useCookies } from 'react-cookie';
import PriceMap from '../product/product-list-algolia/components/AlgoliaPrice/priceMap';
import useLocale from 'helpers/hooks/useLocale';
import { P2Badge } from '../product/product-list-algolia/types';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mobile } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import { Variant } from 'shared/types/product';

type BasicProduct = {
  name?: string;
  image?: string;
  price?: Record<string, unknown>;
  categoryKey?: {
    [locale: string]: string[];
  };
  categories?: {
    [locale: string]: {
      lvl0?: string[];
      [key: string]: any;
    };
  };
  slug?: string;
  standalonePrice?: standalonePrice[];
  offers?: string[] | unknown;
  swatches?: swatches[];
  customFit?: string;
  firstReceiptDate?: string;
  variants?: Variant[];
  colourDisplay?: string;
  variantCode?: string;
  _url?: string;
};

type product = {
  product?: BasicProduct;
  p1BadgeData?: any;
  p2BadgeData?: P2Badge[];
};
const ProductTileComponent = ({ product, p1BadgeData, p2BadgeData }: product) => {
  const { fallbackLocale, localeKey } = useLocale();
  const [isMobile] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
  const stripHtmlTags = (str: string) => str?.replace(/<[^>]*>/g, '');
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const convertProductNameToUrl = (input: string) => {
    const urlformat = `${input?.toLowerCase()?.replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url.replace(/,/g, '');
  };
  const now = new Date();
  const productOffers = product?.offers as Array<{ id: string; description: string }> | undefined;
  const hasOffer = (productOffers?.length ?? 0) > 0;
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const cartDiscountBadge = p1BadgeData?.find((badge: any) => badge?.label?.[selectedLocale] === 'cart discount');
  // const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);
  const cartDiscount: offer[] = (product?.offers as offer[]) || [];
  const filteredCartDiscount = cartDiscount?.filter((i: offer) => i?.id !== '');
  // const productColour = product?.swatches?.[0]?.colour || product?.colourDisplay || '';
  const productColour =
    product?.swatches?.find((s) => s?.variantCode === product?.variantCode)?.colour || product?.colourDisplay || '';
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const mrrpPounds = mrrpPrice / Math.pow(10, 2);
  const pricePounds = price / Math.pow(10, 2);
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price || mrrpPrice < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }
    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);
  // const isNew = useMemo(() => {
  //   const dateStr = product?.variants?.find((v: Variant) => v?.attributes?.firstReceiptDate)?.attributes
  //     ?.firstReceiptDate;
  //   if (!dateStr) return false;
  //   const firstReceipt = new Date(dateStr as string);
  //   if (isNaN(firstReceipt.getTime())) return false;
  //   const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  //   return firstReceipt >= twoMonthsAgo && firstReceipt <= now;
  // }, []);
  const isNew = useMemo(() => {
    const categories = product?.categories?.[selectedLocale]?.lvl0;
    if (!Array.isArray(categories)) return false;
    return categories?.some((category: string) => category?.toLowerCase?.().includes('new arrivals'));
  }, []);
  const isSale = useMemo(() => {
    const categories = (product?.categories as any)?.[selectedLocale]?.lvl0;
    if (!Array.isArray(categories)) return false;

    return categories.some((category: string) => category?.toLowerCase?.().includes('sale'));
  }, []);
  const isCustomFitProduct = product?.customFit === 'custom' || false;
  const activeP1Badges = useMemo(
    () =>
      p1BadgeData?.filter((badgemap: any) => badgemap?.variantCodes?.includes(product?.swatches?.[0]?.variantCode)) ||
      [],
    [p1BadgeData, product],
  );
  const activeP2Badges = [
    isNew ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'new') : null,
    isSale ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'sale') : null,
    isCustomFitProduct ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'customFit') : null,
  ].filter(Boolean);
  const getRanking = (badge: any) => Number(badge?.ranking ?? 99);
  const allBadges = [...(Array.isArray(activeP1Badges) ? activeP1Badges : []), ...activeP2Badges];
  const sortedBadges = allBadges?.sort((a, b) => getRanking(a) - getRanking(b));

  const imageBadges = sortedBadges?.filter(
    (badge) => (isMobile ? badge.visibility?.mobile?.container : badge.visibility?.desktop?.container) === 'image',
  );

  const menuBadges = sortedBadges?.filter(
    (badge) => (isMobile ? badge.visibility?.mobile?.container : badge.visibility?.desktop?.container) === 'menu',
  );
  const getVisibleBadges = (badges: any[]) => {
    const now = new Date();
    return badges.filter((badge) => {
      const { isHidden, validFrom, validTo } = badge.conditions || {};
      if (isHidden) return false;
      const from = validFrom ? new Date(validFrom) : null;
      const to = validTo ? new Date(validTo) : null;
      const fromValid = from instanceof Date && !isNaN(from as any);
      const toValid = to instanceof Date && !isNaN(to as any);
      return (!fromValid || from <= now) && (!toValid || to >= now);
    });
  };

  const visibleImageBadges = getVisibleBadges(imageBadges);
  const groupedImageBadges = visibleImageBadges?.reduce((acc, badge) => {
    const position = isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position;
    if (position && !acc[position]) {
      acc[position] = [badge];
    }
    return acc;
  }, {} as Record<string, any[]>);
  const bottomCenterBadges = {
    'bottom-center': groupedImageBadges['bottom-center'] || [],
  };
  const otherPositionBadges = Object.entries(groupedImageBadges)
    .filter(([position]) => position !== 'bottom-center')
    .reduce((acc, [position, badges]) => {
      acc[position] = badges;
      return acc;
    }, {} as typeof groupedImageBadges);
  const visibleMenuBadges = getVisibleBadges(menuBadges);
  const getPositionClass = (position: string) => {
    switch (position) {
      case 'top-right':
        return 'top-0 right-0 items-end';
      case 'bottom-center':
        return 'bottom-0 left-1/2 transform -translate-x-1/2 items-center';
      case 'left':
        return 'top-1/2 left-0 transform -translate-y-1/2';
      case 'center':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'right':
        return 'top-1/2 right-0 transform -translate-y-1/2';
      default:
        return 'top-0 right-0';
    }
  };

  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);
  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice?.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    const filteredPrice = product?.standalonePrice?.[0] as standalonePrice | undefined;

    if (filteredPrice) {
      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
  }, [product?.standalonePrice, cookies?.locale_selection?.currency]);
  const category = product?.categories?.[localeKey]?.lvl0?.[0] ?? product?.categories?.[fallbackLocale]?.lvl0?.[0];
  return (
    <>
      <div className="flex flex-col items-center justify-between ">
        <SmartLink
          href={`${
            isCustomFitProduct
              ? product?._url
              : product?._url
              ? `${product?._url}?variantCode=${product?.variantCode ?? product?.swatches?.[0]?.variantCode ?? ''}`
              : `/${category?.replace(/\s+/g, '-')}/${
                  convertProductNameToUrl(product?.name as string) + product?.slug + '.html'
                }?variantCode=${product?.variantCode ?? product?.swatches?.[0]?.variantCode ?? ''}`
          }`}
        >
          <div className="relative flex flex-col">
            <div className="relative  bg-white">
              <div className="relative w-full">
                {product?.image && (
                  <Image
                    src={product?.image}
                    suffix="medium"
                    alt={product?.name ?? ''}
                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 sm:h-[164px] md:h-[314px]"
                  />
                )}

                {typeof discountPercentage === 'number' &&
                  discountPercentage > 0 &&
                  typeof mrrpPoundsDiscount === 'number' &&
                  mrrpPoundsDiscount && (
                    <div className="absolute left-0 top-0 z-10 flex h-[20px] min-w-[39px] items-center justify-center gap-8 self-stretch rounded-[2px] border border-[#686868] bg-white px-6 py-8 sm:left-2 sm:top-2 sm:h-[22px] sm:min-w-[46px]">
                      {/* <div className=" h-[20px] min-w-[39px] lg:h-[22px] lg:min-w-[46px]"> */}
                      <p
                        className={`text-center font-body text-11 font-bold not-italic leading-[110%] text-[#686868] sm:text-14`}
                      >
                        {mrrpPounds < 200
                          ? `-${discountPercentage}%`
                          : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                              mrrpPoundsDiscount,
                            )}`}
                      </p>
                      {/* </div> */}
                    </div>
                  )}
                {
                  <div>
                    {hasOffer && isDesktopSize && (
                      <div className="absolute bottom-0 flex w-full flex-col items-center justify-center gap-4">
                        <div
                          style={{
                            backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                            color: cartDiscountBadge?.styles?.textColor,
                            padding: !isDesktopSize
                              ? cartDiscountBadge?.styles?.paddingMobile
                              : cartDiscountBadge?.styles?.paddingDesktop,
                            borderRadius: cartDiscountBadge?.styles?.borderRadius,
                            fontSize: !isDesktopSize
                              ? cartDiscountBadge?.styles?.fontSizeMobilePx
                              : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                            fontWeight: cartDiscountBadge?.styles?.fontWeight,
                            letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                            minHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                            maxHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                          }}
                          className="flex flex-col items-center justify-center whitespace-normal break-words text-center"
                        >
                          {productOffers?.[0]?.description?.split(',')?.map((part, idx, arr) =>
                            idx < arr?.length - 1 ? (
                              <span key={idx}>
                                {part?.trim()}
                                <br />
                              </span>
                            ) : (
                              <span key={idx}>{part?.trim()}</span>
                            ),
                          )}
                        </div>

                        <div className="flex flex-col items-center gap-y-2">
                          {Object.entries(bottomCenterBadges).map(([position, badges]) =>
                            (badges as any[])?.map((item: any, index: number) => {
                              const styles = item?.styles;
                              const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                              const iconAsset = item?.icon?.asset?.[selectedLocale];
                              const iconSize = isDesktopSize
                                ? Math.min(item?.icon?.iconSizeDesktopPx ?? 0, 22)
                                : Math.min(item?.icon?.iconSizeMobilePx ?? 0, 18);
                              const iconPosition = item?.icon?.position || 'left';
                              const iconType = item?.icon?.positioning?.type || 'relative';
                              const iconStyle: React.CSSProperties = {
                                position: iconType,
                                top: item?.icon?.positioning?.top ?? 0,
                                left: item?.icon?.positioning?.left ?? 0,
                                width: iconSize,
                                height: iconSize,
                                ...(iconPosition === 'left' && { marginRight: item?.icon?.spacingPx ?? 0 }),
                                ...(iconPosition === 'right' && { marginLeft: item?.icon?.spacingPx ?? 0 }),
                              };

                              const inlineStyles = {
                                ...(isGradient
                                  ? { backgroundImage: styles?.backgroundGradient }
                                  : { backgroundColor: styles?.backgroundColor }),
                                color: styles?.textColor,
                                fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                                fontWeight: styles?.fontWeight,
                                padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                                minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                                maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                                borderWidth: `${styles?.borderThickness || 0}px`,
                                borderColor: styles?.borderColor,
                                borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                                letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                              };

                              const label = item?.label?.[selectedLocale] || '';
                              const shouldUseLineBreak = isDesktopSize
                                ? styles?.manualLineBreakDesktop
                                : styles?.manualLineBreakMobile;

                              return (
                                <div
                                  key={item?.label?.[selectedLocale] || index}
                                  style={inlineStyles}
                                  className="w-fit"
                                >
                                  {iconPosition === 'left' && iconAsset && (
                                    <img
                                      src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                                      alt=""
                                      width={iconSize}
                                      height={iconSize}
                                      style={iconStyle}
                                    />
                                  )}
                                  {shouldUseLineBreak ? (
                                    <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                                  ) : (
                                    <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                                  )}
                                  {iconPosition === 'right' && iconAsset && (
                                    <img
                                      src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                                      alt=""
                                      width={iconSize}
                                      height={iconSize}
                                      style={iconStyle}
                                    />
                                  )}
                                </div>
                              );
                            }),
                          )}
                        </div>
                      </div>
                    )}
                    {Object.entries(otherPositionBadges).map(([position, badges]) => (
                      <div
                        key={position}
                        className={`absolute flex w-fit flex-col gap-y-4 ${getPositionClass(position)}`}
                      >
                        {(badges as any[])?.map((item: any, index: number) => {
                          const styles = item?.styles;
                          const desktopPosition = item?.visibility?.desktop?.position;
                          const mobilePosition = item?.visibility?.mobile?.position;
                          const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                          const iconPosition = item?.icon?.position || 'left';
                          const iconType = item?.icon?.positioning?.type || 'relative';
                          const iconAsset = item?.icon?.asset?.[selectedLocale];
                          const iconSize = isDesktopSize
                            ? Math.min(item?.icon?.iconSizeDesktopPx ?? 0, 22)
                            : Math.min(item?.icon?.iconSizeMobilePx ?? 0, 18);
                          const iconPositioning = item?.icon?.positioning?.[isMobile ? 'mobile' : 'desktop'] || {
                            top: 0,
                            left: 0,
                          };

                          const inlineStyles = {
                            ...(isGradient
                              ? { backgroundImage: styles?.backgroundGradient }
                              : { backgroundColor: styles?.backgroundColor }),
                            color: styles?.textColor,
                            fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                            fontWeight: styles?.fontWeight,
                            padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                            minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                            maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                            borderWidth: `${styles?.borderThickness || 0}px`,
                            borderColor: styles?.borderColor,
                            borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                            letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                          };
                          const shouldUseLineBreak = isDesktopSize
                            ? styles?.manualLineBreakDesktop
                            : styles?.manualLineBreakMobile;
                          const iconStyle: React.CSSProperties = {
                            position: iconType,
                            top: iconPositioning.top ?? 0,
                            left: iconPositioning.left ?? 0,
                            width: iconSize,
                            height: iconSize,
                            ...(iconPosition === 'left' && { marginRight: item?.icon?.spacingPx ?? 0 }),
                            ...(iconPosition === 'right' && { marginLeft: item?.icon?.spacingPx ?? 0 }),
                          };
                          const iconElement = iconAsset && (
                            <img
                              src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                              alt=""
                              width={iconSize}
                              height={iconSize}
                              style={iconStyle}
                            />
                          );

                          const label = item?.label?.[selectedLocale] || '';

                          return (
                            <div key={item?.label?.[selectedLocale] || index} style={inlineStyles} className="w-fit">
                              {iconPosition === 'left' && iconElement}
                              {shouldUseLineBreak ? (
                                <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                              ) : (
                                <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                              )}{' '}
                              {iconPosition === 'right' && iconElement}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>

            <div className={`flex flex-col gap-y-16 text-center `}>
              <div className="flex flex-col gap-y-8">
                {!isDesktopSize && (
                  <div>
                    {hasOffer && (
                      <div className="flex w-full flex-col items-center justify-center gap-4">
                        <div
                          style={{
                            backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                            color: cartDiscountBadge?.styles?.textColor,
                            padding: !isDesktopSize
                              ? cartDiscountBadge?.styles?.paddingMobile
                              : cartDiscountBadge?.styles?.paddingDesktop,
                            borderRadius: cartDiscountBadge?.styles?.borderRadius,
                            fontSize: !isDesktopSize
                              ? cartDiscountBadge?.styles?.fontSizeMobilePx
                              : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                            fontWeight: cartDiscountBadge?.styles?.fontWeight,
                            letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                            minHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                            maxHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                          }}
                          className="flex flex-col items-center justify-center whitespace-normal break-words text-center"
                        >
                          {productOffers?.[0]?.description?.split(',')?.map((part, idx, arr) =>
                            idx < arr?.length - 1 ? (
                              <span key={idx}>
                                {part?.trim()}
                                <br />
                              </span>
                            ) : (
                              <span key={idx}>{part?.trim()}</span>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {visibleMenuBadges.length > 0 && (
                  <div className="flex flex-col gap-y-8">
                    {visibleMenuBadges?.slice(0, 2)?.map((badge, idx) => {
                      const iconPosition = badge?.icon?.position || 'left';
                      const label = badge?.label?.[selectedLocale];
                      const containerPosition =
                        (isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position) ||
                        'center';
                      const iconType = badge?.icon?.positioning?.type || 'relative';
                      const iconPositioning = badge?.icon?.positioning?.[isDesktopSize ? 'desktop' : 'mobile'] || {
                        top: 0,
                        left: 0,
                      };
                      const iconSize = isDesktopSize
                        ? Math.min(badge?.icon?.iconSizeDesktopPx ?? 0, 22)
                        : Math.min(badge?.icon?.iconSizeMobilePx ?? 0, 18);
                      const styles = badge?.styles;
                      const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                      const shouldUseLineBreak = isDesktopSize
                        ? styles?.manualLineBreakDesktop
                        : styles?.manualLineBreakMobile;
                      const iconStyle: React.CSSProperties = {
                        position: iconType,
                        top: iconPositioning.top ?? 0,
                        left: iconPositioning.left ?? 0,
                        width: iconSize,
                        height: iconSize,
                        ...(iconPosition === 'left' && { marginRight: badge?.icon?.spacingPx ?? 0 }),
                        ...(iconPosition === 'right' && { marginLeft: badge?.icon?.spacingPx ?? 0 }),
                      };
                      const selfClass =
                        containerPosition === 'center'
                          ? 'self-center'
                          : containerPosition === 'right'
                          ? 'self-end'
                          : 'self-start';

                      const iconElement = badge?.icon?.asset && (
                        <img
                          src={`https://${badge?.icon?.asset?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${badge?.icon?.asset?.[selectedLocale]?.name}`}
                          alt=""
                          width={iconSize}
                          height={iconSize}
                          style={iconStyle}
                        />
                      );
                      return (
                        <span
                          key={badge?.id || badge?.badgeText || idx}
                          className={`inline-flex w-fit items-center ${selfClass}`}
                          style={{
                            ...(isGradient
                              ? { backgroundImage: styles?.backgroundGradient }
                              : { backgroundColor: styles?.backgroundColor }),
                            color: styles?.textColor,
                            fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                            fontWeight: styles?.fontWeight,
                            padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                            minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                            maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                            gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                            borderWidth: `${styles?.borderThickness || 0}px`,
                            borderColor: styles?.borderColor,
                          }}
                        >
                          {iconPosition === 'left' && iconElement}
                          {shouldUseLineBreak ? (
                            <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                          ) : (
                            <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                          )}{' '}
                          {iconPosition === 'right' && iconElement}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              {product?.name && (
                <div className="relative  flex-grow overflow-hidden text-ellipsis text-[18px] font-medium leading-[22px] md:text-[18px] md:leading-[22px]">
                  {`${product?.name}${productColour ? ` - ${productColour}` : ''}`}
                </div>
              )}

              <div className="flex flex-col justify-center gap-y-16">
                <div className="mt-4 text-center md:mt-6">
                  <div className="flex flex-nowrap items-center justify-center text-[32px] font-bold leading-[32px] text-[#1e1e1e]">
                    <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SmartLink>
      </div>
    </>
  );
};

export default ProductTileComponent;
